const STORAGE_KEY = "bellame:quote-items";
const UPDATED_EVENT = "quote:updated";

/**
 * @typedef {object} QuoteItem
 * @property {string} id - Stable identifier (variant id when available, else product id).
 * @property {string} productId
 * @property {string} [variantId]
 * @property {string} title
 * @property {string} [variantTitle]
 * @property {string} url
 * @property {string} [image]
 * @property {number} quantity
 * @property {string} note
 */

/**
 * Small localStorage-backed store for the "Request a quote" list.
 * Intentionally independent from the Shopify cart so both can coexist.
 */
const QuoteStore = {
  /** @returns {QuoteItem[]} */
  read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("[quote-list] Failed to read storage", error);
      return [];
    }
  },

  /** @param {QuoteItem[]} items */
  write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn("[quote-list] Failed to write storage", error);
    }
    document.dispatchEvent(
      new CustomEvent(UPDATED_EVENT, { detail: { items } })
    );
  },

  get count() {
    return this.read().reduce((sum, item) => sum + (item.quantity || 1), 0);
  },

  /** @param {QuoteItem} item */
  add(item) {
    const items = this.read();
    const existing = items.find((entry) => entry.id === item.id);
    if (existing) {
      existing.quantity = (existing.quantity || 1) + (item.quantity || 1);
    } else {
      items.push({ quantity: 1, note: "", ...item });
    }
    this.write(items);
  },

  /** @param {string} id */
  remove(id) {
    this.write(this.read().filter((entry) => entry.id !== id));
  },

  /**
   * @param {string} id
   * @param {number} quantity
   */
  setQuantity(id, quantity) {
    const items = this.read();
    const entry = items.find((item) => item.id === id);
    if (!entry) return;
    entry.quantity = Math.max(1, quantity || 1);
    this.write(items);
  },

  /**
   * @param {string} id
   * @param {string} note
   */
  setNote(id, note) {
    const items = this.read();
    const entry = items.find((item) => item.id === id);
    if (!entry) return;
    entry.note = note;
    // Persist without dispatching a re-render (avoids losing textarea focus).
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn("[quote-list] Failed to persist note", error);
    }
  },

  clear() {
    this.write([]);
  },

  has(id) {
    return this.read().some((entry) => entry.id === id);
  },
};

window.BellaMeQuote = QuoteStore;

/**
 * Button that adds the current product to the quote list.
 * Expects data-* attributes describing the product.
 */
class QuoteAddButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button") || this;
    this.button.addEventListener("click", this.handleClick);
    document.addEventListener(UPDATED_EVENT, this.syncState);
    this.syncState();
  }

  disconnectedCallback() {
    document.removeEventListener(UPDATED_EVENT, this.syncState);
  }

  get item() {
    const { productId, variantId, title, variantTitle, vendor, type, sku, url, image } =
      this.dataset;
    return {
      id: variantId || productId,
      productId,
      variantId,
      title,
      variantTitle,
      vendor,
      type,
      sku,
      url,
      image,
      quantity: 1,
      note: "",
    };
  }

  handleClick = (event) => {
    event.preventDefault();
    const item = this.item;
    if (QuoteStore.has(item.id)) {
      QuoteStore.remove(item.id);
    } else {
      QuoteStore.add(item);
    }
  };

  syncState = () => {
    const inList = QuoteStore.has(this.item.id);
    this.classList.toggle("quote-add--in-list", inList);
    const label = this.querySelector("[data-quote-label]");
    if (label) {
      const added = this.dataset.labelAdded;
      const add = this.dataset.labelAdd;
      if (added && add) label.textContent = inList ? added : add;
    }
    if (this.button && this.button !== this) {
      this.button.setAttribute("aria-pressed", inList ? "true" : "false");
    }
  };
}

if (!customElements.get("quote-add-button")) {
  customElements.define("quote-add-button", QuoteAddButton);
}

/**
 * Header bubble that shows how many products are in the quote list.
 */
class QuoteCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(UPDATED_EVENT, this.render);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(UPDATED_EVENT, this.render);
  }

  render = () => {
    const count = QuoteStore.count;
    const target = this.querySelector("[data-quote-count]") || this;
    target.textContent = count < 100 ? String(count) : "99+";
    this.classList.toggle("hidden", count === 0);
  };
}

if (!customElements.get("quote-count")) {
  customElements.define("quote-count", QuoteCount);
}

/**
 * The quote list UI: renders items, per-item note + quantity, and serializes
 * everything into a hidden contact[...] field on submit so Shopify emails it.
 */
class QuoteList extends HTMLElement {
  connectedCallback() {
    this.listEl = this.querySelector("[data-quote-items]");
    this.emptyEl = this.querySelector("[data-quote-empty]");
    this.formWrapEl = this.querySelector("[data-quote-form-wrap]");
    this.payloadEl = this.querySelector("[data-quote-payload]");
    this.form = this.querySelector("form");

    document.addEventListener(UPDATED_EVENT, this.render);
    this.addEventListener("click", this.handleClick);
    this.addEventListener("input", this.handleInput);
    this.addEventListener("change", this.handleChange);
    if (this.form) this.form.addEventListener("submit", this.handleSubmit);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(UPDATED_EVENT, this.render);
  }

  strings() {
    try {
      return JSON.parse(this.dataset.strings || "{}");
    } catch (error) {
      return {};
    }
  }

  render = () => {
    const items = QuoteStore.read();
    const hasItems = items.length > 0;

    if (this.emptyEl) this.emptyEl.classList.toggle("hidden", hasItems);
    if (this.formWrapEl) this.formWrapEl.classList.toggle("hidden", !hasItems);
    if (!this.listEl) return;

    if (!hasItems) {
      this.listEl.innerHTML = "";
      this.updatePayload();
      return;
    }

    const t = this.strings();
    this.listEl.innerHTML = items
      .map((item) => this.renderItem(item, t))
      .join("");
    this.updatePayload();
  };

  renderItem(item, t) {
    const image = item.image
      ? `<img class="quote-item__image" src="${item.image}" alt="" width="88" height="88" loading="lazy">`
      : `<span class="quote-item__image quote-item__image--placeholder" aria-hidden="true"></span>`;

    const meta = [];
    if (item.vendor) meta.push(escapeHtml(item.vendor));
    if (item.type) meta.push(escapeHtml(item.type));
    const metaLine = meta.length
      ? `<span class="quote-item__meta color-subtext">${meta.join(" &middot; ")}</span>`
      : "";
    const variant =
      item.variantTitle && item.variantTitle !== "Default Title"
        ? `<span class="quote-item__variant color-subtext">${escapeHtml(
            item.variantTitle
          )}</span>`
        : "";
    const sku = item.sku
      ? `<span class="quote-item__sku color-subtext">${escapeHtml(
          t.sku_label || "SKU"
        )}: ${escapeHtml(item.sku)}</span>`
      : "";

    const noteLabel = escapeHtml(t.note_label || "Note");
    const notePlaceholder = escapeHtml(t.note_placeholder || "");
    const qtyLabel = escapeHtml(t.quantity_label || "Quantity");
    const removeLabel = escapeHtml(t.remove_label || "Remove");

    return `
      <li class="quote-item" data-quote-item="${escapeAttr(item.id)}">
        <div class="quote-item__media">${image}</div>
        <div class="quote-item__content">
          <div class="quote-item__header">
            <a class="quote-item__title reversed-link" href="${escapeAttr(
              item.url || "#"
            )}">
              <span class="reversed-link__text">${escapeHtml(item.title || "")}</span>
            </a>
            <button
              type="button"
              class="quote-item__remove button-unstyled cursor-pointer"
              data-quote-remove
              aria-label="${removeLabel}"
            >&times;</button>
          </div>
          <div class="quote-item__info">${metaLine}${variant}${sku}</div>
          <div class="quote-item__controls">
            <label class="form-label quote-item__qty-label">
              <span>${qtyLabel}</span>
              <input
                class="form-control quote-item__qty"
                type="number"
                min="1"
                inputmode="numeric"
                value="${Number(item.quantity) || 1}"
                data-quote-qty
              >
            </label>
            <label class="form-label quote-item__note-label">
              <span>${noteLabel}</span>
              <input
                type="text"
                class="form-control quote-item__note w-full"
                placeholder="${notePlaceholder}"
                value="${escapeAttr(item.note || "")}"
                data-quote-note
              >
            </label>
          </div>
        </div>
      </li>
    `;
  }

  itemIdFromEvent(event) {
    const wrap = event.target.closest("[data-quote-item]");
    return wrap ? wrap.getAttribute("data-quote-item") : null;
  }

  handleClick = (event) => {
    if (event.target.closest("[data-quote-remove]")) {
      const id = this.itemIdFromEvent(event);
      if (id) QuoteStore.remove(id);
      return;
    }
    if (event.target.closest("[data-quote-clear]")) {
      event.preventDefault();
      QuoteStore.clear();
    }
  };

  handleInput = (event) => {
    if (event.target.matches("[data-quote-note]")) {
      const id = this.itemIdFromEvent(event);
      if (id) {
        QuoteStore.setNote(id, event.target.value);
        this.updatePayload();
      }
    }
  };

  handleChange = (event) => {
    if (event.target.matches("[data-quote-qty]")) {
      const id = this.itemIdFromEvent(event);
      if (id) QuoteStore.setQuantity(id, parseInt(event.target.value, 10));
    }
  };

  updatePayload = () => {
    if (!this.payloadEl) return;
    const items = QuoteStore.read();
    if (items.length === 0) {
      this.payloadEl.value = "";
      return;
    }
    const totalQty = items.reduce(
      (sum, item) => sum + (parseInt(item.quantity, 10) || 1),
      0
    );
    const header = `Anzahl Positionen: ${items.length} | Gesamtmenge: ${totalQty}`;
    const divider = "----------------------------------------";
    const lines = items.map((item, index) => {
      const parts = [];
      const variant =
        item.variantTitle && item.variantTitle !== "Default Title"
          ? ` [${item.variantTitle}]`
          : "";
      parts.push(`${index + 1}) ${item.title}${variant}`);
      parts.push(`   Menge: ${item.quantity || 1}`);
      if (item.vendor) parts.push(`   Hersteller: ${item.vendor}`);
      if (item.type) parts.push(`   Kategorie: ${item.type}`);
      if (item.sku) parts.push(`   SKU: ${item.sku}`);
      if (item.url) parts.push(`   Link: ${item.url}`);
      if (item.note && item.note.trim())
        parts.push(`   Notiz: ${item.note.trim()}`);
      return parts.join("\n");
    });
    this.payloadEl.value = `${header}\n${divider}\n\n${lines.join(
      `\n\n${divider}\n\n`
    )}`;
  };

  handleSubmit = () => {
    // Ensure the payload reflects the latest notes/quantities before submit.
    this.updatePayload();
  };
}

if (!customElements.get("quote-list")) {
  customElements.define("quote-list", QuoteList);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, "&quot;");
}
